import { getMyPermissionAccess } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { NotAuthorizedError } from '@adornis/baseql/errors/NotAuthorizedError.js';
import { defer, EMPTY, from, type Observable, switchMap } from 'rxjs';
import { CLIENT_SCOPABLE_PERMISSIONS, NexusPermission } from '../platform-rbac/nexus-permissions.js';
import { SmartTeamsPermission } from '../platform-rbac/smartteams-permissions.js';

export function readHasAccess(info: { hasAccess?: boolean; toObject?: () => { hasAccess?: boolean } }): boolean {
  const obj = typeof info.toObject === 'function' ? info.toObject() : info;
  return obj.hasAccess === true;
}

/** Global or scoped to at least one client when `resourceContext` is omitted; per-client when set. */
export async function hasNexusPermission(permission: NexusPermission, resourceContext?: string): Promise<boolean> {
  if (permission === NexusPermission.PartnerSchedulingClientToggle) {
    const info = await getMyPermissionAccess(permission)({
      hasAccess: 1,
      allowedContexts: 1,
    });
    const obj = info.toObject?.() ?? info;
    if (!obj.hasAccess) return false;
    if (resourceContext != null && resourceContext !== '') {
      return (obj.allowedContexts ?? []).includes(resourceContext);
    }
    return true;
  }

  if (resourceContext != null && resourceContext !== '') {
    const info = await getMyPermissionAccess(permission, resourceContext)({ hasAccess: 1 });
    return readHasAccess(info);
  }

  // Client-scopable: role grant alone is not enough for route access — need global or ≥1 context.
  if ((CLIENT_SCOPABLE_PERMISSIONS as readonly string[]).includes(permission)) {
    const info = await getMyPermissionAccess(permission)({
      hasAccess: 1,
      isGlobal: 1,
      allowedContexts: 1,
    });
    const obj = info.toObject?.() ?? info;
    if (!obj.hasAccess) return false;
    return obj.isGlobal === true || (obj.allowedContexts?.length ?? 0) > 0;
  }

  const info = await getMyPermissionAccess(permission)({ hasAccess: 1 });
  return readHasAccess(info);
}

export async function hasAnyNexusPermission(
  permissions: readonly NexusPermission[],
  resourceContext?: string,
): Promise<boolean> {
  for (const permission of permissions) {
    if (await hasNexusPermission(permission, resourceContext)) return true;
  }
  return false;
}

export async function hasNexusSchedulingAccess(clientId?: string): Promise<boolean> {
  return hasNexusPermission(NexusPermission.Scheduling, clientId);
}

export async function assertNexusPermission(
  permission: NexusPermission,
  contextLabel = 'nexus',
  resourceContext?: string,
): Promise<void> {
  if (!(await hasNexusPermission(permission, resourceContext))) {
    throw new NotAuthorizedError(permission, contextLabel);
  }
}

export async function hasPlatformUserManage(): Promise<boolean> {
  const [st, nx] = await Promise.all([
    getMyPermissionAccess(SmartTeamsPermission.UserManage)({ hasAccess: 1 }),
    getMyPermissionAccess(NexusPermission.UserManage)({ hasAccess: 1 }),
  ]);
  return readHasAccess(st) || readHasAccess(nx);
}

export function gateNexusSubscription<T>(permission: NexusPermission, inner: () => Observable<T>): Observable<T> {
  return defer(() => from(hasNexusPermission(permission)).pipe(switchMap(allowed => (allowed ? inner() : EMPTY))));
}

export function gateAnyNexusSubscription<T>(
  permissions: readonly NexusPermission[],
  inner: () => Observable<T>,
): Observable<T> {
  return defer(() => from(hasAnyNexusPermission(permissions)).pipe(switchMap(allowed => (allowed ? inner() : EMPTY))));
}

export function gatePlatformUserManageSubscription<T>(inner: () => Observable<T>): Observable<T> {
  return defer(() => from(hasPlatformUserManage()).pipe(switchMap(allowed => (allowed ? inner() : EMPTY))));
}

export async function assertNexusUserTargetPermission(
  permission: NexusPermission.UsersChangePassword | NexusPermission.UsersImpersonate,
  targetUserId: string,
  contextLabel: string,
): Promise<void> {
  if (await hasNexusPermission(NexusPermission.UserManage)) return;
  if (await hasNexusPermission(permission, targetUserId)) return;
  throw new NotAuthorizedError(permission, contextLabel);
}
